import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarDaysIcon, ClockIcon, VideoCameraIcon, MapPinIcon, CheckCircleIcon, XCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { publicBookingApi, toIsoDay, toIsoMonth, PublicSlot } from '../../services/appointmentService';
import LoadingScreen from '../common/LoadingScreen';
import { usePublicTheme } from '../../hooks/usePublicTheme';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

/**
 * Ügyfél-oldali foglaláskezelés (bejelentkezés nélkül, a megerősítő e-mail linkjéből):
 * részletek, lemondás, átütemezés.
 */
const ManageBookingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { theme, toggle: toggleTheme } = usePublicTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apt, setApt] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [mode, setMode] = useState<'view' | 'reschedule' | 'cancel'>('view');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'cancelled' | 'rescheduled' | null>(null);

  // átütemezés
  const [month, setMonth] = useState(new Date());
  const [day, setDay] = useState<Date | null>(null);
  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [pick, setPick] = useState<PublicSlot | null>(null);
  const [monthDays, setMonthDays] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (mode !== 'reschedule' || !token) return;
    let alive = true;
    setMonthDays(null);
    publicBookingApi.manage({ token, action: 'month', month: toIsoMonth(month) })
      .then(r => { if (alive) setMonthDays(r.data.days || {}); })
      .catch(() => { if (alive) setMonthDays({}); });
    return () => { alive = false; };
  }, [mode, month, token]);

  const load = useCallback(async () => {
    if (!token) { setError('Érvénytelen link'); setLoading(false); return; }
    try {
      const r = await publicBookingApi.manage({ token, action: 'get' });
      setApt(r.data.appointment);
      setSettings(r.data.settings);
    } catch (e: any) {
      setError(e?.message && !/internal/i.test(e.message) ? e.message : 'Nem sikerült betölteni a foglalást');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!day || !token) return;
    setSlotsLoading(true); setPick(null);
    publicBookingApi.manage({ token, action: 'slots', date: toIsoDay(day) })
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [day, token]);

  const startsAt = apt ? new Date(apt.appointmentDate) : null;
  const isPast = startsAt ? startsAt < new Date() : false;
  const canChange = apt && apt.status !== 'cancelled' && !isPast;

  const days = useMemo(() => {
    if (!settings) return [];
    const y = month.getFullYear(), m = month.getMonth();
    const first = new Date(y, m, 1);
    const start = new Date(first); start.setDate(start.getDate() - ((first.getDay() + 6) % 7)); // hétfővel kezd
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const max = new Date(now.getTime() + (settings.advanceBooking?.max || 60) * 86400000);
    const out: { d: Date; cur: boolean; ok: boolean }[] = [];
    const c = new Date(start);
    for (let i = 0; i < 42; i++) {
      const cur = c.getMonth() === m;
      const ok = cur && c >= now && c <= max && !!monthDays && (monthDays[toIsoDay(c)] || 0) > 0;
      out.push({ d: new Date(c), cur, ok });
      c.setDate(c.getDate() + 1);
    }
    return out;
  }, [month, settings, monthDays]);

  const doCancel = async () => {
    setBusy(true);
    try {
      const r = await publicBookingApi.manage({ token: token!, action: 'cancel' });
      setApt(r.data.appointment); setDone('cancelled'); setMode('view');
    } catch (e: any) { setError(e?.message || 'Nem sikerült lemondani'); }
    finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const r = await publicBookingApi.manage({ token: token!, action: 'reschedule', newDate: pick.datetime, baseUrl: window.location.origin });
      setApt(r.data.appointment); setDone('rescheduled'); setMode('view'); setDay(null); setPick(null);
    } catch (e: any) { setError(e?.message || 'Nem sikerült átütemezni'); }
    finally { setBusy(false); }
  };

  if (loading) return <LoadingScreen />;

  const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-[#f7f7f9] dark:bg-[#0b0d12] text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="max-w-xl mx-auto px-4 py-8 sm:py-12">
        <div className="flex justify-end mb-2">
          <button type="button" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Világos mód' : 'Sötét mód'}
            className="w-9 h-9 rounded-lg border border-gray-200 dark:border-[#2a2f3c] text-gray-600 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center">
            {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  if (error && !apt) {
    return (
      <Shell>
        <div className="bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c] p-8 text-center">
          <XCircleIcon className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold">Nem található</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{error}</p>
        </div>
      </Shell>
    );
  }

  const dateStr = startsAt?.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
  const statusLabel: Record<string, string> = { pending: 'Megerősítésre vár', confirmed: 'Megerősítve', completed: 'Lezárva', cancelled: 'Lemondva' };

  return (
    <Shell>
      <div className="mb-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">Foglalás kezelése</p>
        <h1 className="text-2xl font-semibold tracking-tight">{settings?.title || 'Foglalás'}</h1>
      </div>

      {done && (
        <div className={`mb-4 rounded-lg border p-3 text-sm flex items-start gap-2 ${done === 'cancelled' ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-[#2a2f3c] text-gray-700 dark:text-gray-300' : 'bg-primary-50 dark:bg-primary-500/10 border-primary-200 dark:border-primary-500/30 text-primary-800 dark:text-primary-300'}`}>
          <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
          {done === 'cancelled' ? 'A foglalást lemondtuk. Erről e-mailt is küldtünk.' : 'Az új időpontot rögzítettük, és e-mailben megerősítettük.'}
        </div>
      )}
      {error && apt && <div className="mb-4 rounded-lg border border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm p-3">{error}</div>}

      {/* Részletek */}
      <div className="bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c] overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-[#2a2f3c] flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[15px] font-semibold truncate">{apt.clientName}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{apt.clientEmail}</div>
          </div>
          <span className={`px-2 py-1 rounded-md text-xs font-medium flex-shrink-0 ${apt.status === 'cancelled' ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' : apt.status === 'pending' ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300' : 'bg-primary-50 dark:bg-primary-500/15 text-primary-800 dark:text-primary-300'}`}>
            {statusLabel[apt.status] || apt.status}
          </span>
        </div>
        <dl className="px-5 py-3 text-sm divide-y divide-gray-100 dark:divide-[#2a2f3c]">
          <div className="flex items-center gap-3 py-2.5"><CalendarDaysIcon className="w-4 h-4 text-gray-400" /><dt className="text-gray-500 dark:text-gray-400 w-24">Dátum</dt><dd className="font-medium">{dateStr}</dd></div>
          <div className="flex items-center gap-3 py-2.5"><ClockIcon className="w-4 h-4 text-gray-400" /><dt className="text-gray-500 dark:text-gray-400 w-24">Időpont</dt><dd className="font-medium tabular-nums">{apt.appointmentTime} · {apt.duration} perc</dd></div>
          <div className="flex items-center gap-3 py-2.5">
            {apt.meetingType === 'online' ? <VideoCameraIcon className="w-4 h-4 text-gray-400" /> : <MapPinIcon className="w-4 h-4 text-gray-400" />}
            <dt className="text-gray-500 dark:text-gray-400 w-24">Forma</dt>
            <dd className="font-medium min-w-0">
              {apt.meetingType === 'online' ? 'Online' : 'Személyes'}
              {apt.meetingType === 'online' && apt.meetingLink && apt.status !== 'cancelled' && (
                <a href={apt.meetingLink} className="block text-primary-700 text-xs break-all mt-0.5" target="_blank" rel="noopener noreferrer">{apt.meetingLink}</a>
              )}
            </dd>
          </div>
        </dl>
        {canChange && mode === 'view' && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-[#2a2f3c] flex flex-col sm:flex-row gap-2">
            <button onClick={() => { setMode('reschedule'); setMonth(new Date(startsAt!)); }} className="crm-btn crm-btn-primary h-11 sm:h-9 flex-1">Másik időpontot kérek</button>
            <button onClick={() => setMode('cancel')} className="crm-btn crm-btn-secondary h-11 sm:h-9 flex-1 text-red-700">Lemondom</button>
          </div>
        )}
        {isPast && apt.status !== 'cancelled' && <div className="px-5 py-3 border-t border-gray-200 dark:border-[#2a2f3c] text-sm text-gray-500 dark:text-gray-400">Ez az időpont már elmúlt.</div>}
      </div>

      {/* Lemondás megerősítése */}
      {mode === 'cancel' && (
        <div className="mt-4 bg-white dark:bg-[#12151c] rounded-xl border border-red-200 dark:border-red-500/40 p-5">
          <h2 className="text-[15px] font-semibold">Biztosan lemondja a foglalást?</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">A lemondás végleges; új időpontot a foglalási linken tud kérni.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button onClick={doCancel} disabled={busy} className="crm-btn crm-btn-danger h-11 sm:h-9 flex-1">{busy ? 'Folyamatban…' : 'Igen, lemondom'}</button>
            <button onClick={() => setMode('view')} disabled={busy} className="crm-btn crm-btn-secondary h-11 sm:h-9 flex-1">Mégse</button>
          </div>
        </div>
      )}

      {/* Átütemezés */}
      {mode === 'reschedule' && settings && (
        <div className="mt-4 bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c] p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-[15px] font-semibold">Új időpont választása</h2>
            <button onClick={() => { setMode('view'); setDay(null); }} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900">Mégse</button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronLeftIcon className="w-4 h-4" /></button>
              <div className="text-sm font-medium capitalize">{month.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' })}</div>
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronRightIcon className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-500 dark:text-gray-400 mb-1">{['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'].map(d => <div key={d}>{d}</div>)}</div>
            <div className="relative">
            {monthDays === null && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 dark:bg-[#12151c]/70 backdrop-blur-[1px]">
                <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white dark:bg-[#12151c] border border-gray-200 dark:border-[#2a2f3c] shadow-sm text-sm font-medium text-gray-800 dark:text-gray-100">
                  <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-primary-600 animate-spin" /> Szabad napok betöltése…
                </div>
              </div>
            )}
            <div className="grid grid-cols-7 gap-1">
              {days.map(({ d, cur, ok }, i) => {
                const sel = day && d.toDateString() === day.toDateString();
                const loadingCell = monthDays === null && cur;
                return (
                  <button key={i} disabled={!ok || monthDays === null} onClick={() => setDay(d)}
                    className={`h-9 rounded-md text-sm tabular-nums ${loadingCell ? 'animate-pulse bg-gray-100 dark:bg-gray-800 text-transparent' : !cur ? 'text-gray-300 dark:text-gray-700' : ok ? (sel ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100') : 'text-gray-300 dark:text-gray-700 cursor-not-allowed'}`}>
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
            </div>
          </div>
          {day && (
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{day.toLocaleDateString('hu-HU', { month: 'long', day: 'numeric', weekday: 'long' })}</div>
              {slotsLoading ? (
                <div className="relative" aria-busy="true">
                  <div className="absolute inset-0 z-10 flex items-center justify-center">
                    <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white dark:bg-[#12151c] border border-gray-200 dark:border-[#2a2f3c] shadow-sm text-sm font-medium text-gray-800 dark:text-gray-100">
                      <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-primary-600 animate-spin" />
                      Szabad időpontok betöltése…
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 opacity-60">
                    {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-10 rounded-md bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
                  </div>
                </div>
              ) : slots.filter(s => s.isAvailable).length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Erre a napra nincs szabad időpont.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slots.filter(s => s.isAvailable).map(s => (
                    <button key={s.datetime} onClick={() => setPick(s)}
                      className={`h-10 rounded-md border text-sm tabular-nums ${pick?.datetime === s.datetime ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900' : 'border-gray-200 dark:border-[#2a2f3c] hover:border-gray-400 dark:hover:border-gray-500'}`}>
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button onClick={doReschedule} disabled={!pick || busy} className="crm-btn crm-btn-primary h-11 sm:h-9 w-full">
            {busy ? 'Mentés…' : pick ? `Átütemezés: ${day?.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })} ${pick.time}` : 'Válasszon időpontot'}
          </button>
        </div>
      )}
    </Shell>
  );
};

export default ManageBookingPage;
